import type { MyWorkspace, UsageArchetype } from "@closer/shared";
import { USAGE_ARCHETYPE_NAMES, USAGE_ARCHETYPES } from "@closer/shared";
import { Button, useToast } from "@closer/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { tenantKey } from "../lib/tenantQuery";

/**
 * Kaydedilmemiş arketip seçiminin durumu ve onu sunucuya yazan tek mekanizma.
 *
 * ⚠ `bekleyen`, "kullanıcı bir şey işaretledi AMA henüz yazılmadı" demektir; mevcut değere
 * geri dönmek bekleyen SAYILMAZ (gereksiz istek ve gereksiz audit satırı doğururdu).
 */
export interface UsageArchetypeSelection {
  mevcut: UsageArchetype;
  secilen: UsageArchetype | null;
  bekleyen: UsageArchetype | null;
  kaydediliyor: boolean;
  sec: (arketip: UsageArchetype | null) => void;
  /** Ekrandaki "Kaydet" düğmesi için: hatayı bildirime çevirir, fırlatmaz. */
  kaydet: (arketip: UsageArchetype) => void;
  /**
   * Bekleyen seçimi yazar. Bekleyen yoksa HİÇ istek üretmez; sunucu reddederse FIRLATIR —
   * çağıran akış (ör. onboarding kapanışı) ilerlememeyi böyle bilir.
   */
  kaydetBekleyeni: () => Promise<void>;
}

/**
 * KİŞİSEL ÇALIŞMA ALANI KULLANIM BİÇİMİ SEÇİMİ (CLOS-808).
 *
 * ### ⚠ Başarı YALNIZ sunucudan
 *
 * İyimser güncelleme YOK. Sunucu 200 dönmeden ekranda hiçbir şey değişmiyor; kart bunu
 * ayrı bir kabul kriteri olarak sayıyor. Ardından bağlam ve alan listesi TAZELENİYOR:
 * yetenek kararı sunucunun ve menü onu okuyor, bayat bir bağlam kullanıcıya kapalı bir
 * domaini açık gösterirdi.
 *
 * ### ⚠ Seçim durumu neden AYRI bir hook'ta
 *
 * Kaydedilmemiş seçim bileşenin içine kilitliyken, seçiciyi barındıran sayfanın birincil
 * düğmesi (onboarding'de "Başlayalım") o seçimi göremiyor ve sessizce ilerletiyordu
 * (CLOS-860). Durum hook'a çıkınca sayfa bekleyen seçimi kendi akışına dahil edebiliyor;
 * `/tenants` ise hook'u bileşenin içinden kullanmaya devam ediyor.
 *
 * `alan` null olabiliyor: üyelik listesi yüklenirken sayfa henüz kişisel alanı bilmiyor,
 * hook ise koşulsuz çağrılmak zorunda. Alan yokken seçim de yapılamadığından istek üreten
 * hiçbir yol açık kalmıyor.
 */
export function useUsageArchetypeSelection(alan: MyWorkspace | null): UsageArchetypeSelection {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { activeTenantId } = useAuth();
  const [secilen, setSecilen] = useState<UsageArchetype | null>(null);

  const mevcut: UsageArchetype = alan?.usageArchetype ?? "self";

  const kaydet = useMutation({
    mutationFn: async (yeni: UsageArchetype) => {
      /** Alan yokken seçim yüzeyi hiç çizilmiyor; bu dal savunma amaçlı. */
      if (!alan) throw new Error("Çalışma alanı yok");
      return (
        await api<{ usageArchetype: UsageArchetype }>(
          `/api/tenants/${alan.tenantId}/usage-archetype`,
          {
            method: "PATCH",
            tenantId: alan.tenantId,
            body: JSON.stringify({ usageArchetype: yeni }),
          },
        )
      ).data;
    },
    onSuccess: async (sonuc) => {
      setSecilen(null);
      /**
       * ⚠ Alan listesi VE bağlam birlikte tazeleniyor. Yalnız liste tazelenseydi aktif
       * alandaki menü eski yetenek kümesiyle kalır ve kullanıcı kapalı bir domaini açık
       * görmeye devam ederdi (CLOS-813).
       */
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      if (alan && activeTenantId === alan.tenantId) {
        await queryClient.refetchQueries({ queryKey: tenantKey(alan.tenantId, "context") });
      }
      toast.success(
        sonuc.usageArchetype === "independent_lawyer"
          ? "Kullanım biçimi güncellendi; Müvekkiller yüzeyi açıldı"
          : "Kullanım biçimi güncellendi; Müvekkiller yüzeyi kapandı",
      );
    },
    onError: (hata: unknown) =>
      toast.error(
        hata instanceof ApiRequestError ? hata.message : "Kullanım biçimi değiştirilemedi",
      ),
  });

  const bekleyen = secilen !== null && secilen !== mevcut ? secilen : null;

  return {
    mevcut,
    secilen,
    bekleyen,
    kaydediliyor: kaydet.isPending,
    sec: setSecilen,
    kaydet: (arketip) => kaydet.mutate(arketip),
    kaydetBekleyeni: async () => {
      if (bekleyen === null) return;
      await kaydet.mutateAsync(bekleyen);
    },
  };
}

/**
 * Seçim yüzeyi. Durumu DIŞARIDAN alıyor: aynı seçimi hem bu alanlar hem sayfanın birincil
 * düğmesi görebilsin diye (CLOS-860).
 *
 * ### ⚠ Bu bir MESLEK alanı değil
 *
 * Kart açık: *"Bu seçim Profil/Account seviyesinde global meslek alanı olarak
 * gösterilmemeli."* Bu yüzden bileşen hesabın değil ÇALIŞMA ALANININ yanında duruyor ve
 * metni de öyle konuşuyor — "sen kimsin" değil, "bu alanı nasıl kullanıyorsun".
 *
 * ### ⚠ Sonuç ÖNCEDEN söyleniyor
 *
 * Kart *"değişiklik öncesi etki özeti"* istiyor. Seçenek metinleri neyin açılıp
 * kapanacağını yazıyor; kullanıcı Müvekkiller yüzeyinin geleceğini tıklamadan biliyor.
 */
export function UsageArchetypeFields({
  alan,
  secim,
}: {
  alan: MyWorkspace;
  secim: UsageArchetypeSelection;
}) {
  const bekleyen = secim.bekleyen;

  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-border-default p-3">
      <legend className="px-1 text-xs font-semibold tracking-wide text-fg-soft uppercase">
        Bu alanı nasıl kullanıyorsunuz?
      </legend>

      {USAGE_ARCHETYPES.map((arketip) => {
        const isaretli = (secim.secilen ?? secim.mevcut) === arketip;
        return (
          <label
            key={arketip}
            className="flex cursor-pointer items-start gap-2 text-sm text-fg-muted"
          >
            <input
              type="radio"
              name={`arketip-${alan.tenantId}`}
              className="mt-1"
              value={arketip}
              checked={isaretli}
              disabled={secim.kaydediliyor}
              onChange={() => secim.sec(arketip)}
            />
            <span>
              <span className="block font-medium text-fg">{USAGE_ARCHETYPE_NAMES[arketip]}</span>
              <span className="block text-xs">
                {arketip === "independent_lawyer"
                  ? "Kişisel alanınızda müvekkil kaydı tutabilirsiniz. Kurumsal organizasyon oluşturmaz; ekip, departman ve portföy sorumluluğu açılmaz."
                  : "Yalnız kendi sözleşmelerinizi yönetirsiniz. Müvekkiller yüzeyi kapalı kalır."}
              </span>
            </span>
          </label>
        );
      })}

      {bekleyen ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" loading={secim.kaydediliyor} onClick={() => secim.kaydet(bekleyen)}>
            Kaydet
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={secim.kaydediliyor}
            onClick={() => secim.sec(null)}
          >
            Vazgeç
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}

/** Seçimi kendi içinde tutan hâli — `/tenants` listesindeki her kişisel alan için. */
export function UsageArchetypeChooser({ alan }: { alan: MyWorkspace }) {
  const secim = useUsageArchetypeSelection(alan);
  return <UsageArchetypeFields alan={alan} secim={secim} />;
}
